import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';
import { OrganizationService } from '../../../organizations/organization.service';
import { ToastService } from '../../../shared/modules/toast/toast.service';
import {
  BusinessPremise,
  ElectronicDevice,
  Organization,
} from '../../../shared/sdk';
import { FursService } from '../furs.service';

@Component({
  selector: 'app-device-add',
  templateUrl: './device-add.component.html',
  styleUrls: ['./device-add.component.css'],
})
export class DeviceAddComponent implements OnInit, OnDestroy {
  @Input() device: ElectronicDevice;
  @Input() strategy: 'C' | 'B';
  activeOrg: Organization;
  deviceForm: FormGroup;
  editing = false;
  premises: BusinessPremise[];
  sent = false;
  loading = false;
  private onDestroy$: Subject<void> = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private fursService: FursService,
    private orgService: OrganizationService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.deviceForm = this.fb.group({
      electronicDeviceId: [
        '',
        [Validators.required, Validators.pattern('^[a-zA-Z0-9]*$')],
      ],
      businessPremiseId: [null, Validators.required],
      lastNumber: [1, Validators.required],
      environment: [this.device.environment],
    });

    this.editing = this.device && this.device.id;

    if (!this.editing) {
      this.deviceForm
        .get('businessPremiseId')
        .valueChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe((premiseId) => {
          if (premiseId) {
            const premise = this.premises.find((p) => p.id === premiseId);

            this.deviceForm
              .get('electronicDeviceId')
              .patchValue(`D${premise.electronicDevices.length + 1}`);
          } else {
            this.deviceForm.get('electronicDeviceId').setValue('');
          }
        });
    } else {
      // Editing
      this.deviceForm.patchValue(this.device);
      this.deviceForm.controls.businessPremiseId.disable();

      const year = moment().get('year');
      const lastNumbers = this.device.numbers;

      if (lastNumbers && lastNumbers[year]) {
        this.deviceForm.controls.lastNumber.patchValue(lastNumbers[year]);

        if (lastNumbers[year] !== 0) {
          this.deviceForm.controls.lastNumber.disable();
        }
      }
    }

    this.orgService.active
      .pipe(
        concatMap((org: Organization) => {
          this.activeOrg = org;

          return this.fursService.getPremises(this.activeOrg.id, {
            where: {
              provider: 'furs',
            },
            include: 'electronicDevices',
          });
        }),
        map(({ body }) => body),
        takeUntil(this.onDestroy$),
      )
      .subscribe((premises) => {
        this.premises = premises;

        if (this.premises?.length) {
          this.deviceForm
            .get('businessPremiseId')
            .patchValue(this.premises[0].id);
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onSave(): void {
    this.sent = true;

    if (this.deviceForm.valid) {
      this.loading = true;
      const year = moment().get('year');
      const instance = this.deviceForm.value;
      const premiseId = this.deviceForm.get('businessPremiseId').value;
      const premise = this.premises.find((p) => p.id === premiseId);

      if (!instance.numbers) {
        instance.numbers = {};
      }
      instance.numbers[year] = instance.lastNumber - 1;

      if (this.editing) {
        delete instance.id;
      }

      if (this.editing) {
        this.fursService.updateDevice(this.device.id, instance).subscribe(
          (device: ElectronicDevice) => {
            this.loading = false;
            this.toastService.success(
              this.translate.instant('Device successfully updated'),
            );

            device.businessPremise = premise;
            this.activeModal.close(device);
          },
          (err) => {
            this.loading = false;
            this.toastService.warning(
              err.statusCode === 422
                ? this.translate.instant(
                    'Device with this ID already exists on premise',
                  )
                : this.translate.instant(
                    'There was a problem, please try again or contact support.',
                  ),
            );
          },
        );
      } else {
        this.fursService
          .createDevice(
            this.activeOrg.id,
            this.deviceForm.get('businessPremiseId').value,
            instance,
          )
          .subscribe(
            (device: ElectronicDevice) => {
              this.loading = false;
              this.toastService.success(
                this.translate.instant('Device successfully added'),
              );

              device.businessPremise = premise;
              this.activeModal.close(device);
            },
            (err) => {
              this.loading = false;
              this.toastService.warning(
                err.statusCode === 422
                  ? this.translate.instant(
                      'Device with this ID already exists on premise',
                    )
                  : this.translate.instant(
                      'There was a problem, please try again or contact support.',
                    ),
              );
            },
          );
      }
    }
  }
}
